import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteRecord } from "@/components/common/image-viewer-modal/use-delete-record";
import { ApiError, OfflineError } from "@/lib/api";

interface DeleteRecordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
  projectId: string;
  recordId: string;
}

export function DeleteRecordDialog({
  isOpen,
  onClose,
  onDeleted,
  projectId,
  recordId,
}: DeleteRecordDialogProps) {
  const { mutate, isPending } = useDeleteRecord();

  const handleConfirm = () => {
    mutate(
      { projectId, recordId },
      {
        onSuccess: () => {
          onDeleted();
          onClose();
          // Always one: this dialog is bound to a single record. The bulk
          // delete in records-view owns the pluralised copy.
          toast("1 image deleted");
        },
        onError: (error) => {
          // A synced record can only be deleted online (v1). Losing
          // connectivity mid-flow surfaces a typed `OfflineError` from
          // `apiFetch` — show the "reconnect" affordance, never a raw error
          // toast. A pending draft never reaches here (it deletes locally).
          // See [[offline-record-outbox]] H3.
          if (error instanceof OfflineError) {
            toast("Offline — reconnect to delete this record");
            return;
          }
          // Discriminated copy — never leak the raw `error.message`. On
          // Safari a failed `fetch()` throws `TypeError: Load failed`
          // which would otherwise reach the user verbatim.
          if (error instanceof ApiError) {
            toast("Couldn't delete this record — please try again");
          } else {
            toast("Couldn't delete — check your connection and try again");
          }
          // Wrapped as `captureMessage` (not `captureException`) so the
          // report survives `instrument.ts`'s `ignoreErrors: ["Load failed",
          // "Failed to fetch", ...]` — without the wrap, the very class of
          // failure this branch is here to surface (Safari/Firefox fetch
          // errors) is silently dropped at the SDK level. See
          // `specs/conventions/error-reporting.md` § Explicit captureException
          // in mutation handlers.
          Sentry.captureMessage("Delete record request failed", {
            level: "error",
            tags: { area: "record-delete" },
            extra: {
              projectId,
              recordId,
              errorName: error instanceof Error ? error.name : "Unknown",
              errorMessage:
                error instanceof Error ? error.message : String(error),
              status: error instanceof ApiError ? error.status : undefined,
            },
          });
        },
      },
    );
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(o) => (o ? null : onClose())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this record?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the record and all of its photos for everyone on the
            project. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-12" disabled={isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
